import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';

import { SparksAgentService } from '@/components/composite/SparksAgent/services/SparksAgentService';

import { ErrorCustom, get, post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type {
    AiMessage,
    AiMessagesListResponse,
    AiPostMessageResponse,
    AiSendMessageBody
} from '@/components/composite/SparksAgent/types';

import { AI_CONVERSATION_MESSAGES } from '@/lib/routes/apiEndpoints';
import { AI_CONVERSATION_MESSAGES_PAGE_SIZE } from '@/components/composite/SparksAgent/const';

const LANGFUSE_TRACE_HEADER = 'x-langfuse-traceid';

type UseAiConversationMessagesProps = {
    conversationId: string | null;
    limit?: number;
    options?: SWRInfiniteConfiguration<AiMessagesListResponse, ErrorCustom>;
    shouldFetch?: boolean;
};

function getKey(
    pageIndex: number,
    previousPageData: AiMessagesListResponse | null,
    conversationId: string,
    limit: number
) {
    if (!conversationId) return null;
    if (previousPageData && !previousPageData.next_cursor) return null;

    const base = `${injectEndpointUrlParams(AI_CONVERSATION_MESSAGES, {
        CONVERSATION_ID: conversationId
    })}?limit=${limit}`;
    if (pageIndex === 0) return base;
    if (previousPageData?.next_cursor) {
        return `${base}&cursor=${encodeURIComponent(previousPageData.next_cursor)}`;
    }
    return null;
}

export type SendAiMessageResult = {
    data: AiMessage;
    traceId: string | null;
};

export async function sendAiMessage(
    conversationId: string,
    body: AiSendMessageBody
): Promise<SendAiMessageResult> {
    const endpoint = injectEndpointUrlParams(AI_CONVERSATION_MESSAGES, {
        CONVERSATION_ID: conversationId
    });
    const { data: rawBody, headers } = await post<AiPostMessageResponse>(endpoint, body, {
        returnHeaders: [LANGFUSE_TRACE_HEADER]
    });
    const data = SparksAgentService.parseSendMessageResponse(rawBody);
    return { data, traceId: headers[LANGFUSE_TRACE_HEADER] ?? null };
}

export function useAiConversationMessages({
    conversationId,
    limit = AI_CONVERSATION_MESSAGES_PAGE_SIZE,
    options = {},
    shouldFetch = true
}: UseAiConversationMessagesProps) {
    const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
        AiMessagesListResponse,
        ErrorCustom
    >(
        shouldFetch && conversationId
            ? (pageIndex, previousPageData) =>
                  getKey(pageIndex, previousPageData, conversationId, limit)
            : null,
        async (url) => {
            const result = await get<{ data: AiMessagesListResponse }>(url);
            return result?.data as AiMessagesListResponse;
        },
        {
            revalidateOnFocus: false,
            revalidateIfStale: false,
            ...options
        }
    );

    const pages = data?.filter((page): page is AiMessagesListResponse => page !== undefined) ?? [];
    // API returns "latest created" first; for chat we want oldest at top, newest at bottom
    const messages: AiMessage[] = [...pages.flatMap((page) => page.data ?? [])].reverse();
    const loadMore = () => setSize(size + 1);
    const hasMore = Boolean(pages[pages.length - 1]?.next_cursor);

    return {
        messages,
        data,
        error,
        isLoading,
        isLoadingMore: size > 0 && data && typeof data[size - 1] === 'undefined' && isValidating,
        loadMore,
        hasMore,
        mutate
    };
}
